import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Trash } from 'iconsax-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { OnboardingAction, OnboardingState } from '../onboarding.types';
import { FeeType } from '../../../apis/models/students';

interface Step3Props {
  state: OnboardingState;
  dispatch: React.Dispatch<OnboardingAction>;
}

const Step3_Classrooms: React.FC<Step3Props> = ({ state, dispatch }) => {
  const { t, i18n } = useTranslation();
  const isFr = i18n.language.includes('fr');
  const [name, setName] = useState('');
  const [feeCategory, setFeeCategory] = useState<string>(FeeType.YEARLY_FEE_PRIMARY);

  // Use the API fee type labels if available, fall back to enum key
  const studentFeeTypes = useSelector((state: RootState) =>
    state.feeTypes.student.concat(state.feeTypes.other)
  );
  const resolveLabel = (key: string) => {
    const found = studentFeeTypes.find((ft) => ft.enum_key === key);
    if (found) return isFr ? found.value_fr : found.value_en;
    return key;
  };

  const handleAdd = () => {
    if (!name.trim()) return;
    dispatch({
      type: 'SET_CLASSROOMS',
      payload: [...state.classrooms, { name: name.trim(), capacity: 0, feeCategory }],
    });
    setName('');
  };

  const handleRemove = (index: number) => {
    dispatch({
      type: 'SET_CLASSROOMS',
      payload: state.classrooms.filter((_, i) => i !== index),
    });
  };

  const feeTypeOptions = Object.values(FeeType);

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {t('onboarding.classrooms_hint')}
      </Typography>

      <Grid container spacing={2} alignItems="flex-end" mb={3}>
        <Grid item xs={12} sm={5}>
          <TextField
            fullWidth
            size="small"
            label={t('onboarding.classroom_name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          />
        </Grid>
        <Grid item xs={12} sm={5}>
          <FormControl fullWidth size="small">
            <InputLabel>{t('onboarding.classroom_fee_category')}</InputLabel>
            <Select
              value={feeCategory}
              label={t('onboarding.classroom_fee_category')}
              onChange={(e) => setFeeCategory(e.target.value)}
            >
              {feeTypeOptions.map((ft) => (
                <MenuItem key={ft} value={ft}>
                  {resolveLabel(ft)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={2}>
          <Button variant="outlined" onClick={handleAdd} fullWidth disabled={!name.trim()}>
            {t('onboarding.btn_add')}
          </Button>
        </Grid>
      </Grid>

      {state.classrooms.length === 0 ? (
        <Typography color="text.secondary" variant="body2">
          {t('onboarding.no_classrooms')}
        </Typography>
      ) : (
        <Stack spacing={1}>
          {state.classrooms.map((classroom, i) => (
            <Box
              key={i}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
            >
              <Box>
                <Typography variant="body1">{classroom.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {resolveLabel(classroom.feeCategory)}
                </Typography>
              </Box>
              <IconButton size="small" color="error" onClick={() => handleRemove(i)}>
                <Trash size={16} />
              </IconButton>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default Step3_Classrooms;
